import { useEffect, useMemo, useState } from 'react';
import { useLoaderData, NavLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  BookOpenIcon,
  ClipboardDocumentIcon,
  CubeIcon,
  HandRaisedIcon,
  ListBulletIcon,
  PlusIcon,
  Square3Stack3DIcon,
  SparklesIcon,
  StarIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { slotFor } from '@/lib/slot-color';
import { toaster } from '@/components/ui/toaster';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import PrintFieldSelector from '@/components/ui/print-field-selector';
import Carousel from '@/components/carousel';
import BlockContentType from '@/components/content/block-content-type';
import ContentAuthor from '@/components/content/content-author';
import CommentSection from '@/components/comments/comment-section';
import WorkspaceShell from '@/components/layout/workspace-shell';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import {
  BlockType,
  Content,
  ContentTypeFieldView,
  File,
  FileType,
  StaticField,
} from '@/lib/types';

import '@/styles/print.css';

type LoaderData = {
  content: Content;
  subContents: any;
  contentTypeFields: ContentTypeFieldView[];
  workspaceCreatorEmail: string;
  workspaceName: string;
  workspaceLogo?: string;
  workspaceBannerImage?: string;
  contentCreatorName?: string;
};

type FieldInfo = {
  key: string;
  type: BlockType;
  label: string;
  options: Map<string, string>;
  showAsMetaPill: boolean;
};

const SECTION_ICONS: Array<{ match: RegExp; icon: typeof CubeIcon; palette: string }> = [
  { match: /equip|gear|material/i, icon: CubeIcon, palette: 'orange' },
  { match: /setup|set\s*up/i, icon: Square3Stack3DIcon, palette: 'green' },
  { match: /coach|teach|cue|tip/i, icon: HandRaisedIcon, palette: 'red' },
  { match: /variation|variant|option/i, icon: SparklesIcon, palette: 'purple' },
  { match: /instruction|step/i, icon: ListBulletIcon, palette: 'blue' },
  { match: /key\s*point|highlight/i, icon: StarIcon, palette: 'yellow' },
  { match: /drill|exercise|practice/i, icon: WrenchScrewdriverIcon, palette: 'cyan' },
  { match: /description|overview|summary/i, icon: BookOpenIcon, palette: 'gray' },
];

const FALLBACK_ICONS: Array<{ icon: typeof CubeIcon; palette: string }> = [
  { icon: CubeIcon, palette: 'orange' },
  { icon: Square3Stack3DIcon, palette: 'green' },
  { icon: HandRaisedIcon, palette: 'red' },
  { icon: SparklesIcon, palette: 'purple' },
  { icon: ListBulletIcon, palette: 'blue' },
  { icon: StarIcon, palette: 'yellow' },
];

function pickSectionIcon(label: string, fallbackIndex: number) {
  const found = SECTION_ICONS.find((entry) => entry.match.test(label));
  if (found) return { icon: found.icon, palette: found.palette };
  const fb = FALLBACK_ICONS[fallbackIndex % FALLBACK_ICONS.length];
  return { icon: fb.icon, palette: fb.palette };
}

const getFileType = (fileUrl: string): FileType.Image | FileType.Video => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
  const extension = fileUrl.split('.').pop()?.toLowerCase();
  if (extension && videoExtensions.includes(extension)) return FileType.Video;
  return FileType.Image;
};

function buildFieldInfo(field: ContentTypeFieldView): FieldInfo {
  let options = new Map<string, string>();
  if (field.field_type === BlockType.Selection && field.field_options?.length) {
    options = field.field_options.reduce((m, o) => {
      m.set(o.option_value, o.option_label);
      return m;
    }, new Map<string, string>());
  }
  const props = (field as any).properties || {};
  return {
    key: field.referenceID,
    type: field.field_type,
    label: field.field_name,
    options,
    showAsMetaPill: !!props.showAsMetaPill,
  };
}

function pillValue(field: FieldInfo, raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (field.type === BlockType.Selection && Array.isArray(raw)) {
    const labels = raw
      .map((v) => (field.options.get(v as string) ?? (v as string)))
      .filter(Boolean);
    return labels.length ? labels.join(', ') : null;
  }
  if (typeof raw === 'string') {
    const stripped = raw.replace(/<[^>]+>/g, '').trim();
    return stripped || null;
  }
  return String(raw);
}

export default function PublicContent() {
  const data = useLoaderData() as LoaderData;
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();

  const [files, setFiles] = useState<File[]>([]);
  const [printableFields, setPrintableFields] = useState<string[]>([]);

  useEffect(() => {
    const list = data?.content?.media_files?.files;
    if (!Array.isArray(list)) {
      setFiles([]);
      return;
    }
    setFiles(list.map((src: string) => ({ type: getFileType(src), src })));
  }, [data?.content?.media_files]);

  const fields = useMemo<FieldInfo[]>(() => {
    if (!data?.content?.content || !data?.contentTypeFields) return [];

    const content = { ...data.content };
    if (content.description) {
      content.content[StaticField.Description] = { value: content.description };
    }
    const presentRefs = new Set(Object.keys(content.content || {}));
    const sorted = [...data.contentTypeFields].sort((a, b) => a.sort_order - b.sort_order);

    return sorted.reduce<FieldInfo[]>((acc, field) => {
      if (field.field_type === BlockType.SectionTitle) {
        acc.push(buildFieldInfo(field));
        return acc;
      }
      if (field.field_unique_name === StaticField.Description) {
        field.referenceID = field.field_unique_name;
      }
      if (field.referenceID && presentRefs.has(field.referenceID)) {
        acc.push(buildFieldInfo(field));
      }
      return acc;
    }, []);
  }, [data]);

  // Selection fields render as compact pills by default — they're already
  // short, single-value answers and look better up top than as a list section.
  // The showAsMetaPill flag opts other field types in (Text, Numeric, Date).
  const isPill = (f: FieldInfo) => f.showAsMetaPill || f.type === BlockType.Selection;

  const metaPills = useMemo(() => {
    return fields
      .filter(isPill)
      .map((f) => {
        const raw = data?.content?.content?.[f.key]?.value;
        const value = pillValue(f, raw);
        return value ? { ...f, value } : null;
      })
      .filter(Boolean) as Array<FieldInfo & { value: string }>;
  }, [fields, data]);

  const bodyFields = useMemo(() => fields.filter((f) => !isPill(f)), [fields]);

  const fieldOptions = useMemo(() => {
    const opts = bodyFields.map((field) => ({
      id: field.key,
      label: field.label,
      type: BlockType[field.type] || 'Other',
    }));
    if (files.length > 0) {
      opts.unshift({ id: 'media-files', label: 'Images & Videos', type: 'Media' });
    }
    return opts;
  }, [bodyFields, files.length]);

  const shouldPrintField = (id: string) =>
    printableFields.length === 0 || printableFields.includes(id);

  const handlePrintWithFields = (selected: string[]) => {
    setPrintableFields(selected);
    setTimeout(() => window.print(), 300);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toaster.create({ type: 'success', title: 'Link copied to clipboard' });
    } catch {
      toaster.create({ type: 'error', title: 'Could not copy link' });
    }
  };

  const isDisplayComment =
    (isAdmin || isUser) &&
    workspaceId &&
    data?.content?.workspace_id &&
    parseInt(workspaceId, 10) === data.content.workspace_id;

  const contentTypeName = data?.content?.content_type_name;

  return (
    <WorkspaceShell
      workspaceName={data?.workspaceName}
      workspaceLogo={data?.workspaceLogo}
      workspaceBannerImage={data?.workspaceBannerImage}
    >
      <Container maxW='5xl' px={{ base: 4, md: 6 }} mt={{ base: -6, md: -10 }} pb={10}>
        <Box
          bg='bg.panel'
          borderRadius='lg'
          shadow={{ base: 'sm', md: 'lg' }}
          p={{ base: 5, md: 8 }}
          className='print-card'
        >
          {/* Breadcrumb */}
          <Box mb={3} className='no-print'>
            <Breadcrumbs
              items={
                contentTypeName
                  ? [{ label: 'Libraries' }, { label: contentTypeName }]
                  : [{ label: 'Libraries' }]
              }
            />
          </Box>

          {/* Title */}
          <Heading
            as='h1'
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight='bold'
            letterSpacing='-0.01em'
            mb={4}
          >
            {data?.content?.name}
          </Heading>

          {/* Author row */}
          <ContentAuthor
            name={data?.contentCreatorName}
            updatedAt={(data?.content as any)?.updated_at}
          />

          {/* Hero media */}
          {files.length > 0 && shouldPrintField('media-files') && (
            <Box
              mb={6}
              borderRadius='lg'
              overflow='hidden'
              className='media-container'
            >
              <Carousel items={files} />
            </Box>
          )}

          {/* Meta pills */}
          {metaPills.length > 0 && (
            <Flex
              gap={3}
              wrap='wrap'
              mb={6}
              className='no-print'
            >
              {metaPills.map((p) => {
                const slot = slotFor(p.label);
                return (
                  <Box
                    key={p.key}
                    px={4}
                    py={3}
                    borderRadius='lg'
                    bg={`slot${slot}.bg`}
                    borderWidth='1px'
                    borderColor='border'
                    minW='100px'
                  >
                    <Text
                      fontSize='label'
                      textTransform='uppercase'
                      letterSpacing='wider'
                      color={`slot${slot}.fg`}
                      fontWeight='semibold'
                      mb={1}
                    >
                      {p.label}
                    </Text>
                    <Text fontSize='sm' fontWeight='semibold' color='fg.strong'>
                      {p.value}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          )}

          {/* Action buttons */}
          <Flex
            gap={2}
            wrap='wrap'
            mb={6}
            pb={6}
            borderBottomWidth='1px'
            borderColor='border'
            className='no-print'
          >
            <Button size='sm' variant='outline' onClick={handleCopyLink}>
              <Icon as={ClipboardDocumentIcon} w={4} h={4} />
              Copy link
            </Button>
            <Button size='sm' variant='outline' asChild>
              <NavLink to='/auth/login'>
                <Icon as={PlusIcon} w={4} h={4} />
                Save to my Coach Hub
              </NavLink>
            </Button>
            {fieldOptions.length > 0 && (
              <Box ml='auto'>
                <PrintFieldSelector
                  fields={fieldOptions}
                  onPrint={handlePrintWithFields}
                  buttonText='Print'
                />
              </Box>
            )}
          </Flex>

          {/* Body sections */}
          <Stack gap={6}>
            {bodyFields.map((field, i) => {
              if (!shouldPrintField(field.key)) {
                return (
                  <Box key={field.key} className='no-print' display='none' />
                );
              }
              const { icon: SectionIcon } = pickSectionIcon(field.label, i);
              const slot = slotFor(field.label);
              return (
                <Box key={field.key} className='print-field'>
                  <Flex align='center' gap={3} mb={2}>
                    <Flex
                      w='28px'
                      h='28px'
                      borderRadius='md'
                      bg={`slot${slot}.bg`}
                      color={`slot${slot}.fg`}
                      align='center'
                      justify='center'
                      flexShrink={0}
                    >
                      <Icon as={SectionIcon} w={4} h={4} />
                    </Flex>
                    <Heading as='h3' fontSize='md' fontWeight='semibold'>
                      {field.label}
                    </Heading>
                  </Flex>
                  <Box pl={10}>
                    <BlockContentType
                      block={{ type: field.type, label: field.label, options: field.options } as any}
                      content={
                        field.type === BlockType.Link
                          ? data.content?.content?.[field.key]
                          : data.content?.content?.[field.key]?.value || ''
                      }
                      subContents={data.subContents}
                      isPublic={true}
                      hideLabel={true}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {isDisplayComment && (
            <Box mt={8} className='no-print'>
              <CommentSection contentId={data.content?.id} />
            </Box>
          )}
        </Box>

        {/* Managed-by alert — info = brand per DS --info: brand.soft bg + brand.fg text */}
        {data?.workspaceName && data?.workspaceCreatorEmail && (
          <Alert.Root
            status='info'
            borderRadius='lg'
            mt={6}
            className='no-print'
            bg='brand.soft'
            borderWidth='1px'
            borderColor='brand.solid'
            color='brand.fg'
          >
            <Alert.Indicator color='brand.solid' />
            <Alert.Title color='brand.fg'>
              This content is managed by {data.workspaceName}. To edit this content or create your
              own, contact{' '}
              <Link
                href={`mailto:${data.workspaceCreatorEmail}`}
                color='brand.solid'
                fontWeight='semibold'
                variant='underline'
              >
                {data.workspaceCreatorEmail}
              </Link>{' '}
              and ask to join their workspace on Coach Hub.
            </Alert.Title>
          </Alert.Root>
        )}
      </Container>
    </WorkspaceShell>
  );
}
